import { defineMessages } from 'react-intl'

const scope = `procurify.components.SettingsImport`

export default defineMessages({
  breadcrumbSettings: {
    id: `${scope}.breadcrumbSettings`,
    defaultMessage: 'Settings',
  },
  breadcrumbImport: {
    id: `${scope}.breadcrumbImport`,
    defaultMessage: 'Import Data',
  },
  firstSectionHeader: {
    id: `${scope}.firstSectionHeader`,
    defaultMessage: 'Setting up your Procurify account?',
  },
  firstSectionSubheader: {
    id: `${scope}.firstSectionSubheader`,
    defaultMessage: `Follow our {numSteps}-step import file flow below. The simplest way to do this is by using the templates provided.`,
  },
  firstSectionSubheader2: {
    id: `${scope}.firstSectionSubheader2`,
    defaultMessage: `If you'd like some help importing your data, check out`,
  },
  ourGuideLink: {
    id: `${scope}.ourGuideLink`,
    defaultMessage: 'our guide.',
  },
  returningUserHeader: {
    id: `${scope}.returningUserHeader`,
    defaultMessage: `Are you a returning user wanting to import data?`,
  },
  returningUserSubheader: {
    id: `${scope}.returningUserSubheader`,
    defaultMessage: `Locate the particular section of Procurify you'd like to import and click 'Import CSV File'.`,
  },
  approverSectionHeader: {
    id: `${scope}.approverSectionHeader`,
    defaultMessage: 'Approving Requests',
  },
  approverSectionSubheader: {
    id: `${scope}.approverSectionSubheader`,
    defaultMessage:
      'Once you’ve completed the steps above it’s time to set up your {approvalRouting} to make sure the right people are receiving the right requests.',
  },
  accountSectionHeader: {
    id: `${scope}.accountSectionHeader`,
    defaultMessage: 'Chart of Accounts and Budgets',
  },
  accountSectionSubheader: {
    id: `${scope}.accountSectionSubheader`,
    defaultMessage: `The last required piece of your Procurify set up is Account Codes, which you can then use to set up your budgets. Budgets aren’t mandatory to get started but highly recommended to keep you on the right track.`,
  },
  accountSectionSubheaderNoBudgets: {
    id: `${scope}.accountSectionSubheaderNoBudgets`,
    defaultMessage: `The last required piece of your Procurify set up is Account Codes.`,
  },
  accountSectionHeaderNoBudgets: {
    id: `${scope}.accountSectionHeaderNoBudgets`,
    defaultMessage: 'Chart of Accounts',
  },
  accountSectionHeaderWithBudgets: {
    id: `${scope}.accountSectionHeaderWithBudgets`,
    defaultMessage: 'Chart of Accounts and Budgets',
  },
  accountSectionHeaderWithBudgetCategories: {
    id: `${scope}.accountSectionHeaderWithBudgetCategories`,
    defaultMessage: 'Chart of Accounts and Budget Categories',
  },
  accountSectionSubheaderWithBudgetCategories: {
    id: `${scope}.accountSectionSubheaderWithBudgetCategories`,
    defaultMessage: `The last required piece of your Procurify set up is Account Codes. You may optionally set up Budget Categories to track and budget your spending.`,
  },
  thirdSectionHeader: {
    id: `${scope}.thirdSectionHeader`,
    defaultMessage: 'What else?',
  },
  thirdSectionSubheader: {
    id: `${scope}.thirdSectionSubheader`,
    defaultMessage:
      'The basics of your system are set up! You may also want to use the following imports:',
  },
  primaryOrgCategoryHeader: {
    id: `${scope}.primaryOrgCategoryHeader`,
    defaultMessage: '{singularPrimaryOrgCategoryLabel}',
  },
  primaryOrgCategoryDescription: {
    id: `${scope}.primaryOrgCategoryDescription`,
    defaultMessage:
      'Import your {pluralPrimaryOrgCategoryLabel} first. This will help you organize the rest of your Procurify data.',
  },
  primaryOrgCategoryModalHeader: {
    id: `${scope}.primaryOrgCategoryModalHeader`,
    defaultMessage: '{singularPrimaryOrgCategoryLabel} Importing CSV Tool',
  },
  shippingAddressHeader: {
    id: `${scope}.shippingAddressHeader`,
    defaultMessage: 'Shipping Addresses',
  },
  shippingAddressDescription: {
    id: `${scope}.shippingAddressDescription`,
    defaultMessage:
      'If any of your {pluralPrimaryOrgCategoryLabel} have multiple shipping addresses you can import them here.',
  },
  shippingAddressModalHeader: {
    id: `${scope}.shippingAddressModalHeader`,
    defaultMessage: 'Shipping Address Importing CSV Tool',
  },
  secondaryOrgCategoryHeader: {
    id: `${scope}.secondaryOrgCategoryHeader`,
    defaultMessage: '{pluralSecondaryOrgCategoryLabel}',
  },
  secondaryOrgCategoryDescription: {
    id: `${scope}.secondaryOrgCategoryDescription`,
    defaultMessage:
      'After you’ve imported {pluralPrimaryOrgCategoryLabel} above, add your {pluralSecondaryOrgCategoryLabel} under each {singularPrimaryOrgCategoryLabel}. This will structure your system to make sure that every employee only sees what they need to see.',
  },
  secondaryOrgCategoryModalHeader: {
    id: `${scope}.secondaryOrgCategoryModalHeader`,
    defaultMessage: '{singularSecondaryOrgCategoryLabel} Importing CSV Tool',
  },
  userHeader: {
    id: `${scope}.userHeader`,
    defaultMessage: 'Users',
  },
  userDescription: {
    id: `${scope}.userDescription`,
    defaultMessage:
      'Next you’ll need to add everyone in your organization that will use Procurify. Each user will be assigned a specific role that matches their duties within your organization.',
  },
  userModalHeader: {
    id: `${scope}.userModalHeader`,
    defaultMessage: 'User Importing CSV Tool',
  },
  roleHeader: {
    id: `${scope}.roleHeader`,
    defaultMessage: 'Roles',
  },
  roleDescription: {
    id: `${scope}.roleDescription`,
    defaultMessage:
      'In order to use Procurify, each user must have a role enabled, along with assigned permissions and {pluralPrimaryOrgCategoryLabel}. You can add roles for your users with this template.',
  },
  roleModalHeader: {
    id: `${scope}.roleModalHeader`,
    defaultMessage: 'Role Importing CSV Tool',
  },
  permissionHeader: {
    id: `${scope}.permissionHeader`,
    defaultMessage: 'User Permissions',
  },
  permissionDescription: {
    id: `${scope}.permissionDescription`,
    defaultMessage:
      'Customize what users have access to by importing permissions. Check out what permissions are available {here1} or learn more about importing user permissions {here2}.',
  },
  permissionModalHeader: {
    id: `${scope}.permissionModalHeader`,
    defaultMessage: 'Permission Importing CSV Tool',
  },
  approverHeader: {
    id: `${scope}.approverHeader`,
    defaultMessage: 'Approvers',
  },
  approverDescription: {
    id: `${scope}.approverDescription`,
    defaultMessage:
      'Once you’ve imported the groups in the previous step, it’s time to import users into those groups. Make sure to have your list of Approval Routing Groups from the step above when working with this template.',
  },
  approverModalHeader: {
    id: `${scope}.approverModalHeader`,
    defaultMessage: 'Approver Importing CSV Tool',
  },
  approvalRoutingText: {
    id: `${scope}.approvalRoutingText`,
    defaultMessage: 'Approval Routing',
  },
  approvalRoutingGroupHeader: {
    id: `${scope}.approvalRoutingGroupHeader`,
    defaultMessage: 'Approval Routing Groups',
  },
  approvalRoutingGroupDescription: {
    id: `${scope}.approvalRoutingGroupDescription`,
    defaultMessage:
      "Before adding your approvers, you'll need to import your approval groups. These groups designate a select set of approvers to review and approve/deny requests (this may include order, travel, and expense requests, new vendor requests and/or fund requests).",
  },
  approvalRoutingGroupDescriptionNoPurchasing: {
    id: `${scope}.approvalRoutingGroupDescriptionNoPurchasing`,
    defaultMessage:
      "Before adding your approvers, you'll need to import your approval groups. These groups designate a select set of approvers to review and approve/deny requests (this may include travel and expense requests and/or fund requests).",
  },
  approvalRoutingGroupModalHeader: {
    id: `${scope}.approvalRoutingGroupModalHeader`,
    defaultMessage: 'Approval Routing Group Importing CSV Tool',
  },
  accountHeader: {
    id: `${scope}.accountHeader`,
    defaultMessage: 'Chart of Accounts (Account Codes)',
  },
  accountDescription: {
    id: `${scope}.accountDescription`,
    defaultMessage: `Use this template to import your organization’s Chart of Accounts. If you'd like to set up budgets in Procurify you'll need to add Account Codes first.`,
  },
  accountDescriptionSansBudget: {
    id: `${scope}.accountDescriptionSansBudget`,
    defaultMessage: `Use this template to import your organization’s Chart of Accounts.`,
  },
  accountModalHeader: {
    id: `${scope}.accountModalHeader`,
    defaultMessage: 'Chart of Accounts Importing CSV Tool',
  },
  accountCodeLinkHeader: {
    id: `${scope}.accountCodeLinkHeader`,
    defaultMessage: 'Link Account Codes to Organizational Categories',
  },
  accountCodeLinkDescription: {
    id: `${scope}.accountCodeLinkDescription`,
    defaultMessage:
      'Use this template to link your account codes to different {pluralPrimaryOrgCategoryLabel} & {pluralSecondaryOrgCategoryLabel}.',
  },
  accountCodeLinkModalHeader: {
    id: `${scope}.accountCodeLinkModalHeader`,
    defaultMessage: 'Account Code Link Importing CSV Tool',
  },
  budgetSetupHeader: {
    id: `${scope}.budgetSetupHeader`,
    defaultMessage: 'Budget setup',
  },
  budgetSetupDescription: {
    id: `${scope}.budgetSetupDescription`,
    defaultMessage: 'Use this template to create and configure your budgets.',
  },
  budgetSetupModalHeader: {
    id: `${scope}.budgetSetupModalHeader`,
    defaultMessage: 'Budgets Setup Importing CSV Tool',
  },
  budgetCategoryHeader: {
    id: `${scope}.budgetCategoryHeader`,
    defaultMessage: 'Budget Categories',
  },
  budgetCategoryDescription: {
    id: `${scope}.budgetCategoryDescription`,
    defaultMessage:
      "Use this template to create your organization's Budget Categories and configure your budgets.",
  },
  budgetCategoryModalHeader: {
    id: `${scope}.budgetCategoryModalHeader`,
    defaultMessage: 'Budget Category Importing CSV Tool',
  },
  budgetHeader: {
    id: `${scope}.budgetHeader`,
    defaultMessage: 'Budgets',
  },
  budgetDescription: {
    id: `${scope}.budgetDescription`,
    defaultMessage: 'Import current budgets into Procurify here.',
  },
  budgetModalHeader: {
    id: `${scope}.budgetModalHeader`,
    defaultMessage: 'Budget Importing CSV Tool',
  },
  plannedBudgetHeader: {
    id: `${scope}.plannedBudgetHeader`,
    defaultMessage: 'Scheduled Budgets',
  },
  plannedBudgetDescription: {
    id: `${scope}.plannedBudgetDescription`,
    defaultMessage: 'Import future budgets into Procurify here.',
  },
  plannedBudgetModalHeader: {
    id: `${scope}.plannedBudgetModalHeader`,
    defaultMessage: 'Planned Budget Importing CSV Tool',
  },
  vendorHeader: {
    id: `${scope}.vendorHeader`,
    defaultMessage: 'Vendors',
  },
  vendorDescription: {
    id: `${scope}.vendorDescription`,
    defaultMessage:
      'Import the details of any vendors you’re working with here.',
  },
  vendorModalHeader: {
    id: `${scope}.vendorModalHeader`,
    defaultMessage: 'Vendor Importing CSV Tool',
  },
  catalogItemHeader: {
    id: `${scope}.catalogItemHeader`,
    defaultMessage: 'Product Catalog',
  },
  catalogItemDescription: {
    id: `${scope}.catalogItemDescription`,
    defaultMessage: `Already have a catalog of products that you would like to use in Procurify? You're one step ahead. Import it here.`,
  },
  catalogItemModalHeader: {
    id: `${scope}.catalogItemModalHeader`,
    defaultMessage: 'Product Catalog Importing CSV Tool',
  },
  catalogPermissionHeader: {
    id: `${scope}.catalogPermissionHeader`,
    defaultMessage: 'Catalog Permissions',
  },
  catalogPermissionDescription: {
    id: `${scope}.catalogPermissionDescription`,
    defaultMessage:
      'Will specific items only be available for request by members of certain departments? Use this template.',
  },
  catalogPermissionModalHeader: {
    id: `${scope}.catalogPermissionModalHeader`,
    defaultMessage: 'Catalog Permission Importing CSV Tool',
  },
  uploadModalTitle: {
    id: `${scope}.uploadModalTitle`,
    defaultMessage: 'Upload {title} CSV',
  },
  learnMore: {
    id: `${scope}.learnMore`,
    defaultMessage: 'Learn more',
  },
  here: {
    id: `${scope}.here`,
    defaultMessage: 'here',
  },
  organizationalCategorySectionHeader: {
    id: `${scope}.organizationalCategorySectionHeader`,
    defaultMessage:
      '{pluralPrimaryOrgCategoryLabel} and {pluralSecondaryOrgCategoryLabel}',
  },
})
